/*
 * This is a proof of concept. Using various ChatGPT Models we are able to identify items from images, research local market prices and write ads.
 * There are issues but it seems to be functioning well for what it is asked to do.
 */

import { useState } from 'react';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { ForSaleImageIdentify } from './ForSaleImageIdentify';

/* A tab for researching used-item prices and generating a friendly
   building-specific ad.  Workflow:
   1. Pick city  →  2. (optional) upload photo  →  3. price research
   4. show price summary  →  5. generate / edit / publish ad          */
export function ForSaleAdTab() {
  /* ---------- state ---------- */
  
  const [aiAnalysis, setAiAnalysis] = useState(false);
  const [city, setCity] = useState<'Vancouver' | 'Austin' | 'New York'>(
    'Vancouver'
  );
  const [itemDetails, setItemDetails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [priceResearch, setPriceResearch] = useState<string | null>(null);
  const [priceSummary, setPriceSummary] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [listingPrice, setListingPrice] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [showAdToggle, setShowAdToggle] = useState(false);
  const [adText, setAdText] = useState<string | null>(null);
  const [isAdLoading, setIsAdLoading] = useState(false);

  const [showImageSwitch, setShowImageSwitch] = useState(false);
  const [adImages, setAdImages] = useState<string[]>([]);

  const [adStyle, setAdStyle] = useState<
    'professional' | 'facts' | 'casual'
  >('professional');
  const [adLength, setAdLength] = useState<'short' | 'medium' | 'long'>(
    'short'
  );

  const [isEditingAd, setIsEditingAd] = useState(false);

  // AI Analysis is now handled with useEffect inside ForSaleImageIdentify
  const [editedAdText, setEditedAdText] = useState('');
  const [priceWarning, setPriceWarning] = useState('');

  /* ---------- helpers ---------- */
  function handleCitySelect(c: typeof city) {
    setCity(c);
  }

  function handleSubmit() {
    handlePriceResearch(new Event('submit') as unknown as React.FormEvent);
  }

  async function handlePriceResearch(eOrDetails?: React.FormEvent | string) {
    let details = itemDetails;
    if (typeof eOrDetails === 'string') {
      details = eOrDetails;
    } else if (eOrDetails && typeof eOrDetails !== 'string') {
      eOrDetails.preventDefault();
    }
    setIsLoading(true);
    setError(null);
    setPriceResearch(null);
    setShowAdToggle(false);
    setAdText(null);

    try {
      const res = await fetch('/api/price-research', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemDetails: details, city }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to get price research');

      setPriceResearch(data.answer);
      setPriceSummary(data.summary ?? '');
      setPriceRange(data.priceRange ?? '');
      setListingPrice(
        data.priceRange
          ? data.priceRange.split(/[–-]/)[0].replace(/[^\d.]/g, '').trim()
          : ''
      );
      setShowAdToggle(true);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAdRequest() {
    setIsAdLoading(true);
    setError(null);
    setAdText(null);

    try {
      const res = await fetch('/api/generate-forsale-ad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemDetails,
          city,
          images: adImages,
          adStyle,
          adLength,
          price: listingPrice,
          
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate ad');

      setAdText(data.ad || 'No ad generated.');
      // Don't overwrite adImages with the API response
      // This ensures AI-identified images are preserved
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setIsAdLoading(false);
    }
  }

  /* ---------- UI ---------- */
  return (
    <>
      <div className="min-h-screen flex flex-col items-center bg-gray-50 pt-2 overflow-x-hidden w-full">
        <ErrorBoundary>
        {/* ── 1. top controls ───────────────────────────────────────── */}
        <div className="space-y-4 w-full max-w-md px-2 sm:max-w-lg md:max-w-2xl lg:max-w-3xl xl:max-w-4xl mt-2">
          <div className="text-center text-lg font-semibold text-gray-700">
            Where do you live?
          </div>

          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="flex flex-col xs:flex-row gap-2 xs:gap-4 justify-center w-full">
              {(['Vancouver', 'Austin', 'New York'] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => handleCitySelect(c)}
                  className={`px-4 py-2 rounded-md font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    city === c
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  aria-pressed={city === c}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── 2. price research form ──────────────────────────────── */}
        <form
          onSubmit={handlePriceResearch}
          className="w-full max-w-md sm:max-w-xl mx-auto bg-white rounded-lg shadow p-4 sm:p-6 flex flex-col gap-3 sm:gap-4"
        >
          <label className="font-medium text-gray-700 w-full">
            What are you selling?{' '}
            <span className="text-xs text-gray-400">
              (Include name, brand, model, age, wear, etc.)
            </span>
            <textarea
              required
              rows={4}
              value={itemDetails}
              onChange={(e) => setItemDetails(e.target.value)}
              placeholder="e.g. Dyson V11 vacuum, 2 yrs old, lightly used, includes all accessories"
              className="mt-2 w-full border border-gray-300 rounded-md px-3 py-3 text-base focus:ring-2 focus:ring-blue-400 resize-none"
              style={{ minHeight: 44 }}
            />
          </label>

          {/* photo / AI switches */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center mt-4 w-full">
            <button
              type="button"
              className={`w-full sm:w-auto min-h-[44px] px-4 py-2 rounded font-semibold shadow focus:outline-none focus:ring-2 transition-colors ${showImageSwitch ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
              aria-pressed={showImageSwitch}
              onClick={() => setShowImageSwitch((v) => !v)}
            >
              Add photos
            </button>
          </div>

          {/* image upload UI */}
          {showImageSwitch && (
            <div className="w-full px-2 sm:max-w-2xl sm:mx-auto mb-10 overflow-x-hidden">
              <ForSaleImageIdentify
                aiAnalysis={aiAnalysis}
                onIdentified={(desc, images) => {
                  setItemDetails(desc);
                  setAdImages(images ?? []);
                  if (desc && desc.trim()) {
                    setDetailsError(null); // Clear error when AI result is used
                  }
                }}
                onConfirmWithPrice={(desc, _images) => {
                  // Direct path: AI confirmation → price research
                  if (desc && desc.trim()) {
                    setDetailsError(null);
                    handlePriceResearch(desc);
                  }
                }}
              />
            </div>
          )}

          

          <button
            type="button"
            className={`w-full sm:w-auto min-h-[44px] px-4 py-2 rounded font-semibold shadow focus:outline-none focus:ring-2 transition-colors mb-2 ${aiAnalysis && showImageSwitch ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} ${!showImageSwitch ? 'opacity-60 cursor-not-allowed' : ''}`}
            aria-pressed={aiAnalysis}
            onClick={() => {
              if (showImageSwitch) {
                // Simply toggle the AI Analysis state
                // The ForSaleImageIdentify component will automatically
                // detect this change and trigger analysis if images exist
                setAiAnalysis((v) => !v);
              }
            }}
            disabled={!showImageSwitch}
          >
            AI Analysis
          </button>
          <div className="flex flex-col w-full items-center">
            <button
              type="button"
              onClick={() => {
                if (!itemDetails.trim()) {
                  setDetailsError('Please describe what you\'re selling first, either by entering details above or using AI Analysis with photos.');
                  setTimeout(() => setDetailsError(null), 5000); // Clear after 5 seconds
                } else {
                  setDetailsError(null);
                  handleSubmit();
                }
              }}
              disabled={isLoading}
              className="w-full sm:w-auto py-3 px-4 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 min-h-[44px]"
            >
              {isLoading ? 'Searching prices…' : 'Find Typical Selling Price'}
            </button>
            {detailsError && (
              <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-2 text-sm mt-2 max-w-md animate-fade-in">
                {detailsError}
              </div>
            )}
          </div>
        </form>

        {/* ── 3. error ────────────────────────────────────────────── */}
        {error && (
          <div className="max-w-xl mx-auto text-red-600 bg-red-50 border border-red-200 rounded-md p-4 text-sm mt-4">
            {error}
          </div>
        )}

        {/* ── 4. price results & listing price input ─────────────── */}
        {priceResearch && (
          <div className="w-full max-w-md sm:max-w-xl bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mt-4">
            <h3 className="text-lg font-semibold mb-2">What we found:</h3>

            {priceSummary && (
              <div className="mb-2 text-gray-800 text-base">{priceSummary}</div>
            )}
            {priceRange && (
              <div className="mb-2 text-blue-700 font-semibold">
                Suggested price range: {priceRange}
              </div>
            )}

            <pre className="whitespace-pre-line text-gray-800 text-base font-sans mb-2">
              {priceResearch}
            </pre>

            <div className="mt-4 flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full">
              <div>
                <label className="block font-medium text-gray-700 mb-1">
                  Your listing price:
                </label>
                <input
                  type="text"
                  value={listingPrice}
                  onChange={(e) => {
                    setListingPrice(e.target.value);
                    if (e.target.value.trim()) setPriceWarning('');
                  }}
                  placeholder={priceRange ? `e.g. ${priceRange}` : 'Enter price'}
                  className="w-full sm:w-40 border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-blue-400 min-h-[44px]"
                />
              </div>
              {priceWarning.trim() !== '' && (
                <div className="text-red-600 text-sm font-medium whitespace-nowrap mt-6">{priceWarning}</div>
              )}
            </div>
          </div>
        )}

        {/* ── 5. ask to generate ad ─────────────────────────────── */}
        {showAdToggle && !adText && (
          <>
            <div className="w-full max-w-md sm:max-w-xl mx-auto flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mt-4">
              <span className="text-gray-700 font-medium">
                Would you like an ad written for building residents?
              </span>

              <div className="flex items-center gap-2">
                <span className="text-gray-600">Length:</span>
                <select
                  value={adLength}
                  onChange={(e) =>
                    setAdLength(e.target.value as typeof adLength)
                  }
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
              </div>

              <button
                onClick={() => {
                  if (!listingPrice.trim()) {
                    setPriceWarning('Price needs to be added before ad can be written.');
                    return;
                  }
                  setPriceWarning('');
                  handleAdRequest();
                }}
                disabled={isAdLoading}
                className="ml-auto px-4 py-3 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                {isAdLoading ? 'Writing Ad…' : 'Write My Ad'}
              </button>
              {priceWarning.trim() !== '' && (
                <div className="text-red-600 text-sm mt-2 font-medium">{priceWarning}</div>
              )}
            </div>
            {/* Writing style buttons after price research */}
            <div className="w-full max-w-md sm:max-w-xl mx-auto flex flex-wrap gap-2 justify-center mt-2">
              <span className="text-gray-700 font-medium mr-2">Writing style:</span>
              {['professional', 'facts', 'casual'].map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => setAdStyle(style as typeof adStyle)}
                  className={`px-3 py-1 rounded font-semibold border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    adStyle === style
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-100'
                  }`}
                  aria-pressed={adStyle === style}
                >
                  {style === 'professional' ? 'Professional' : style === 'facts' ? 'Just the Facts' : 'Casual'}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── 6. ad preview / edit / publish ─────────────────────── */}
        {adText && (
          <div className="w-full max-w-md sm:max-w-xl mx-auto bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6 mt-4">
            <h3 className="text-lg font-semibold mb-2 text-green-700">
              Your Building Ad
            </h3>

            {/* preview vs edit */}
            {isEditingAd ? (
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 mb-3"
                rows={4}
                value={editedAdText}
                onChange={(e) => setEditedAdText(e.target.value)}
              />
            ) : (
              <>
                {adImages.length > 0 && (
                  <div className="w-full mb-4 flex flex-col gap-2">
                    {adImages.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`ad-img-${i}`}
                        className="w-full max-w-xs sm:max-w-full max-h-60 sm:max-h-80 object-cover rounded-lg border mx-auto"
                        style={{ objectPosition: 'center', minHeight: 44 }}
                      />
                    ))}
                  </div>
                )}
                <pre className="whitespace-pre-line text-gray-900 mb-3">
                  {adText}
                </pre>
                <div className="text-lg font-semibold text-blue-800 mt-2 border-t pt-2">
                  Price: {listingPrice.trim() ? (listingPrice.trim().startsWith('$') ? listingPrice.trim() : `$${listingPrice.trim()}`) : 'N/A'}
                </div>
              </>
            )}


            {/* action buttons */}
            <div className="mt-4 flex flex-wrap gap-2 sm:gap-3 justify-center w-full">
              {!isEditingAd ? (
                <button
                  className="w-full sm:w-auto px-4 py-3 rounded bg-yellow-100 text-yellow-800 font-semibold hover:bg-yellow-200 border border-yellow-200 min-h-[44px]"
                  onClick={() => {
                    setIsEditingAd(true);
                    setEditedAdText(adText);
                  }}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    className="w-full sm:w-auto px-4 py-3 rounded bg-green-600 text-white font-semibold hover:bg-green-700 border border-green-700 min-h-[44px]"
                    onClick={() => {
                      setIsEditingAd(false);
                      setAdText(editedAdText);
                    }}
                  >
                    Save
                  </button>
                  <button
                    className="w-full sm:w-auto px-4 py-3 rounded bg-gray-400 text-white font-semibold hover:bg-gray-500 border border-gray-500 min-h-[44px]"
                    onClick={() => {
                      setIsEditingAd(false);
                      setEditedAdText(adText);
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
              <button
                className="w-full sm:w-auto px-4 py-3 rounded bg-green-600 text-white font-semibold hover:bg-green-700 border border-green-700 min-h-[44px]"
                onClick={() => alert('Publish feature coming soon!')}
              >
                Publish
              </button>
            </div>
          </div>
        )}

        </ErrorBoundary>
      </div>
    </>
  );
}